import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Skeleton } from "../../../components/ui/skeleton";
import { Input } from "../../../components/ui/input";
import ShortenedText from "../../../components/ShortenedText";
import { FaExclamationTriangle, FaTrash } from "react-icons/fa";
import {GoUnverified, GoCheckCircleFill, GoVerified, GoXCircleFill} from "react-icons/go";
import { UserIcon, Star } from "lucide-react";
import { toast } from "react-toastify";
import { DataTable } from "../../../components/DataTable";
import {
  profileService,
  userService,
  userDataMap,
  ServiceOnMount,
} from "../../../services";
import { createItemStore } from "../../../stores/createItemStore";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import Modal from "../../../components/Modal";
import useUserStore2 from "../../../stores/userStore2";
import LoadingImage from "../../../components/LoadingImage"

// Zustand store instantiation
const storeName = "users";
const useItems = createItemStore(
  storeName,
  (selectedUser) => selectedUser.userId,
  (args) => userService.getUserDetails(args[0]),
  userDataMap
);

const columns = [
  {
    accessorKey: "profileId",
    header: "Profile ID",
    cell: ({ row }) => <ShortenedText text={row.getValue("profileId")} />,
  },
  {
    accessorKey: "name",
    header: "Profile Name",
    cell: ({ row }) => <span>{row.getValue("name")}</span>,
  },
  {
    accessorKey: "profStatus",
    header: "Status",
    cell: ({ row }) => (
      <div
        className={`text-sm font-semibold py-1 px-3 rounded-3xl inline-block ${
          row.getValue("profStatus") === "A"
            ? "text-[#16A34A] bg-[#DCFCE7]"
            : row.getValue("profStatus") === "S"
            ? "text-[#EF4444] bg-[#FEE2E2]"
            : "text-[#EF4444] bg-[#FEE2E2]"
        }`}
      >
        {row.getValue("profStatus") === "A"
          ? "Active"
          : row.getValue("profStatus") === "S"
          ? "Suspended"
          : row.getValue("profStatus") === "D"
          ? "Deactivated"
          : "Deactivated"}
      </div>
    ),
  },
  {
    accessorKey: "dateCreated",
    header: "Date Created",
    cell: ({ row }) => (
      <span>
        {new Date(row.getValue("dateCreated")).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Primary",
    cell: ({ row }) => (
      <div
        className={`text-sm font-semibold py-0.5 px-2 rounded-3xl inline-flex items-center gap-1 ${
          row.getValue("status") === "Primary"
            ? "text-green-600 bg-yellow-400"
            : "text-transparent bg-transparent"
        }`}
      >
        {row.getValue("status") === "Primary" && (
          <GoCheckCircleFill className="h-3 w-3" />
        )}
        {row.getValue("status") === "Primary" ? "Primary" : ""}
      </div>
    ),
  },
];

const ViewUserPage = () => {
  const { userId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [reload, setReload] = useState(false);
  const [formData, setFormData] = useState({
    userId: "",
    name: "",
    email: "",
    status: "Active",
    role: "",
  });
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user } = useUserStore2();

  // Zustand store and page hydration
  const { fetchItems, getSortedFiltered } = useItems((state) => state);
  const data = getSortedFiltered(
    (selectedUser) => selectedUser.userId === userId
  );
  const selectedUser = data?.[0] ?? null;

  useEffect(() => {
    fetchItems({}, [userId]);
  }, [userId]);

  const getUserProfiles = ServiceOnMount(
    () => profileService.getUserProfiles(userId),
    (item) => ({
      profileId: item._id,
      name: item.name,
      dateCreated: item.create_date,
      status: item.type === "P" ? "Primary" : "",
      profStatus: item.status,
      memberships: item.memberships || [],
    }),
    [reload]
  );

  const profiles = getUserProfiles.data || [];

  useEffect(() => {
    if (selectedUser && !isEditing) {
      setFormData({
        userId: selectedUser.userId,
        name: selectedUser.userName,
        email: selectedUser.userEmail,
        status:
          selectedUser.status === "Active" ? "A"
            : selectedUser.status === "Pending" ? "P"
            : selectedUser.status === "Suspended" ? "S"
              : selectedUser.status === "Deactivated" ? "D"
              : "D",
        role: userService.roleToCode(selectedUser.role),
      });
    }
  }, [selectedUser, isEditing]);

  const handleSave = async () => {
    try {
      setLoading(true);
      // Enable/Disable selectedUser based on status
      if (formData.status === "A" || formData.status === "Active") {
        await userService.enableUser(formData.userId);
      } else if (formData.status === "S" || formData.status === "Suspended") {
        await userService.suspendUser(formData.userId);
      } else if (formData.status === "D" || formData.status === "Deactivated") {
        await userService.disableUser(formData.userId);
      }

      // Reset the selectedUser role
      if (formData.role) {
        await userService.resetEntityRole({
          EntityId: formData.userId,
          Role: formData.role,
        });
      }

      // Refresh the user data
      await fetchItems({}, [userId]);

      setIsEditing(false);
      setLoading(false);
      toast.success("🎉 User details updated successfully!", {
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error updating selectedUser:", error);
      toast.error("❌ Failed to update selectedUser details", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleStatusChange = (statusOption) => {
    let apiStatus = "";
    switch (statusOption) {
      case "Active":
        apiStatus = "A";
        break;

      case "Suspended":
        apiStatus = "S";
        break;
      case "Deactivated":
        apiStatus = "D";
        break;
      default:
        apiStatus = "A";
    }

    setFormData((prevData) => ({
      ...prevData,
      status: apiStatus,
    }));
  };

  const handleProfileStatusChange = async (entityId, profileId, status) => {
    try {
      await profileService.editProfile({
        EntityId: entityId,
        ProfileId: profileId,
        Status: status,
      });
      toast.success("🎉 Profile Status updated successfully!", {
        autoClose: 2000,
      });
      setReload((prev) => !prev);
    } catch (error) {
      console.error("Failed to update profile status:", error);
      toast.error("❌ Failed to update profile status", {
        autoClose: 2000,
      });
    }
  };

  const handlePasswordReset = async () => {
    try {
      setLoading(true);
      await userService.forgotPasswordRequest(selectedUser.userEmail);
      toast.success("Password reset email sent successfully!", {
        autoClose: 2000,
      });
      setIsPasswordModalOpen(false);
    } catch (err) {
      console.error("Error sending password reset email:", err);
      setError(err.message || "Failed to send password reset email");
      toast.error("Failed to send password reset email", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = (profile) => {
    const baseActions = [
      // {
      //   label: "View Details",
      //   icon: <FaEye />,
      //   onClick: () => navigate(`/edit-profile/${profile.profileId}`),
      //   variant: "dropPlain",
      // },
      {
        label: "Set as Primary",
        icon: <Star />,
        onClick: async () => {
          try {
            await profileService.editProfile({
              EntityId: userId,
              ProfileId: profile.profileId,
              IsPrimary: true,
            });
            toast.success("🎉 Profile set as primary!", { autoClose: 2000 });
            setReload((prev) => !prev);
          } catch (error) {
            console.error("Error setting profile as primary:", error);
          }
        },
        variant: "dropPlain",
      },
    ];

    if (isEditing) {
      baseActions.push(
        {
          label: "Activate",
          icon: <GoCheckCircleFill />,
          onClick: () =>
            handleProfileStatusChange(userId, profile.profileId, "A"),
          variant: "dropGreen",
        },
        {
          label: "Suspend",
          icon: <GoXCircleFill />,
          onClick: () =>
            handleProfileStatusChange(userId, profile.profileId, "S"),
          variant: "dropRed",
        },
        {
          label: "Deactivate",
          icon: <FaTrash />,
          onClick: () =>
            handleProfileStatusChange(userId, profile.profileId, "D"),
          variant: "dropRed",
        }
      );
    }

    return baseActions;
  };

  const tableData = profiles.map((profile) => ({
    ...profile,
    original: profile,
  }));

  // if (selectedUser === null) {
  //   return <p className="text-red-500">Failed to load selectedUser details.</p>;
  // }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="p-8 flex flex-col">
        <div className="bg-white shadow p-6 rounded-lg">
          {/* Header with buttons */}
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-semibold text-gray-900">
              User Details
            </h1>
            <div className="flex gap-3">
              {!isEditing ? (
                <Button onClick={() => setIsEditing(true)}>Edit</Button>
              ) : (
                <>
                  <Button
                    variant="hollowGreen"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={loading}>
                    {loading ? "Saving..." : "Save"}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Three-column layout */}
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_1fr] gap-8">
            {/* Column 1: Profile Picture (fixed width) */}
            <div className="flex flex-col items-center space-y-6">
              {/* Profile Picture - content remains the same */}
              <div className="relative">
                <div className="h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center">
                  {selectedUser?.profilePicture ? (
                    <img
                      src={selectedUser.profilePicture}
                      alt="User Profile"
                      className="h-full w-full rounded-full object-cover"
                      onError={(e) => {
                        e.target.src = "";
                        e.target.className = "hidden";
                      }}
                    />
                  ) : (
                    <UserIcon className="h-8 w-8 text-gray-500" />
                  )}
                </div>
              </div>

              {/* Date Created */}
              <div className="w-full flex flex-col items-center">
                <Label className="mb-0 pb-0">Created Date</Label>{" "}
                {/* Remove bottom margin/padding */}
                <div className="text-sm text-gray-700 px-3 rounded text-center mt-1">
                  {" "}
                  {/* Reduced top margin */}
                  {selectedUser?.createDate ? (
                    new Date(selectedUser.createDate).toLocaleDateString(
                      "en-GB",
                      {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      }
                    )
                  ) : (
                    <Skeleton className="w-24 h-4" />
                  )}
                </div>
              </div>
            </div>

            {/* Column 2: First set of form fields */}
            <div className="space-y-4">
              <div>
                <Label>Status</Label>
                {isEditing ? (
                  <div className="flex flex-wrap gap-2">
                    {["Active", "Suspended", "Deactivated"].map(
                      (statusOption) => {
                        const apiStatus =
                          statusOption === "Active" ? "A"
                            : statusOption === "Suspended" ? "S"
                            : "D";

                        return (
                          <button
                            key={statusOption}
                            onClick={() => handleStatusChange(statusOption)}
                            className={`w-full sm:w-32 px-4 py-2 border font-semibold text-sm transition-all ${
                              formData.status === apiStatus
                                ? statusOption === "Active"
                                  ? "bg-[#14B8A6] text-white" // Green for Active
                                  : statusOption === "Suspended"
                                  ? "text-white bg-red-500" // Yellow for Suspended
                                  : "text-white bg-gray-400" // Red for Deactivated
                                : "bg-white text-black border-gray-300 hover:bg-[#F0FDFA]"
                            }`}
                          >
                            {statusOption}
                          </button>
                        );
                      }
                    )}
                  </div>
                ) : (
                  <Input
                    value={
                      formData.status === "A" ? "Active"
                        : formData.status === "S" ? "Suspended"
                        : formData.status === "D" && selectedUser?.verified ? "Deactivated"
                        : "Invitation sent"
                    }
                    disabled
                  />
                )}
              </div>
              <div>
                <Label>Designation</Label>
                {isEditing ? (
                  <Select
                    name="role"
                    value={formData.role}
                    onValueChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "role",
                          value: value,
                        },
                      })
                    }
                    disabled={userId === user?.id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a Role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">Super Admin</SelectItem>
                      <SelectItem value="POCA">POC Attendant</SelectItem>
                      <SelectItem value="C">Collector</SelectItem>
                      <SelectItem value="U">Public</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={
                      formData.role === "A"
                        ? "Super Admin"
                        : formData.role === "POCA"
                        ? "POC Attendant"
                        : formData.role === "C"
                        ? "Collector"
                        : "Public"
                    }
                    disabled
                  />
                )}
              </div>
              {/* Change Password Button (only for own account) */}
              {userId === selectedUser?.userId && (
                <div className="pt-4 mt-4 w-full">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-semibold">
                        Request Password Reset
                      </div>
                      <div className="text-sm text-gray-500">
                        Send password reset link to selectedUser
                      </div>
                    </div>
                    <Button
                      onClick={() => setIsPasswordModalOpen(true)}
                      className="whitespace-nowrap ml-auto"
                    >
                      Change Password
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Column 3: Second set of form fields */}
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled
                  className="bg-gray-100"
                />
              </div>
              <div className="relative">
                <Label>Email</Label>
                <div className="relative">
                  <Input
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled
                    className="bg-gray-100 pr-12 sm:pr-20" // Less padding on mobile, more on larger screens
                  />
                  {
                    selectedUser?.verified ? (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-green-600">
                        <GoVerified className="h-5 w-5 font-bold" />
                        <span className="hidden sm:inline-block text-sm font-semibold">
                          VERIFIED
                        </span>
                      </div>
                    ) : (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-red-600">
                        <GoUnverified className="h-5 w-5 font-bold" />
                        <span className="hidden sm:inline-block text-sm font-semibold">
                          UNVERIFIED
                        </span>
                      </div>
                    )
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Profiles Table Section */}
          <div className="border p-4 mt-4 w-full rounded-lg">
            <div className="text-base sm:text-lg font-semibold text-[#111827]">
              Profiles List
            </div>
            {
              getUserProfiles.loading ? (
                <LoadingImage />
              ) : profiles.length > 0 ? (
                <DataTable
                  columns={columns}
                  data={tableData}
                  actions={actions}
                />
              ) : (
                <div className="text-center text-gray-500 py-6">
                  No profiles found.
                </div>
              )
            }
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {isPasswordModalOpen && (
        <Modal
          isOpen={isPasswordModalOpen}
          onClose={() => {
            setIsPasswordModalOpen(false);
            setError("");
          }}
          icon={<FaExclamationTriangle />}
          iconColor="red"
          title="Reset Password"
          actions={
            <div className="flex justify-between w-full gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsPasswordModalOpen(false);
                  setError("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handlePasswordReset}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Email"}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="relative">
              Are you sure you would like to reset this selectedUser's password?
              A confirmation email will be sent to: {selectedUser.userEmail}
            </div>
            {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ViewUserPage;
